// Shared Scriptable prelude: theme, data fetch, helpers, header. Each widget script = prelude + body + footer.
export const prelude = ({ host, key, title, tapUrl, comment }) => `// Thrifty Curator — ${title} Widget (Scriptable)
// Best as a LARGE widget. ${comment}
const APP_HOST = "${host}";
const API = "https://" + APP_HOST + "/api/widget/glance?key=${key}";
const SITE_URL = "https://" + APP_HOST + "/admin";
const MESSAGES_URL = "https://" + APP_HOST + "/admin?openMessages=true";
const NOTIFICATIONS_URL = "https://" + APP_HOST + "/admin?openNotifications=true";
const HIDE_TEAM = ["Matthew Guzman"];
// ---- Theme (matches the app) ----
const C = {
  bg1: "#1A1A2E", bg2: "#16213E", bg3: "#0F1626",
  cyan: "#00D4FF", purple: "#8B5CF6", pink: "#FF1493", green: "#10B981", amber: "#FFB800",
  text: "#FFFFFF", dim: "#A7ADC2", faint: "#636A85", card: "#FFFFFF"
};
const col = (hex, a) => (a == null ? new Color(hex) : new Color(hex, a));
const fam = config.widgetFamily || "large";
const isLarge = fam === "large" || fam === "extraLarge";
const isSmall = fam === "small";
let data = null;
let keyRejected = false;
try { data = await new Request(API).loadJSON(); } catch (e) {}
if (data && data.detail) { keyRejected = true; data = null; }
const w = new ListWidget();
const bg = new LinearGradient();
bg.colors = [col(C.bg1), col(C.bg2), col(C.bg3)];
bg.locations = [0, 0.5, 1];
bg.startPoint = new Point(0, 0);
bg.endPoint = new Point(1, 1);
w.backgroundGradient = bg;
w.setPadding(8, 10, 6, 10);
w.url = ${tapUrl};
const REFRESH_MINUTES = 5;
w.refreshAfterDate = new Date(Date.now() + REFRESH_MINUTES * 60 * 1000);
// ---- helpers ----
const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "now";
  if (m < 60) return m + "m";
  const h = Math.floor(m / 60);
  return h < 24 ? h + "h" : Math.floor(h / 24) + "d";
};
const exact = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mmdd = (iso) => (iso ? iso.slice(5, 10).replace("-", "/") : "");
const formatHoursMins = (decimalHours) => {
  const h = Number(decimalHours) || 0;
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  if (hrs === 0) return mins + "m";
  if (mins === 0) return hrs + "h";
  return hrs + "h " + mins + "m";
};
function text(stack, str, size, color, opts = {}) {
  const t = stack.addText(String(str));
  t.font = opts.weight === "heavy" ? Font.heavySystemFont(size)
        : opts.weight === "bold" ? Font.boldSystemFont(size)
        : opts.weight === "semi" ? Font.semiboldSystemFont(size)
        : Font.systemFont(size);
  t.textColor = col(color, opts.alpha);
  t.lineLimit = opts.lines == null ? 1 : opts.lines;
  if (opts.scale) t.minimumScaleFactor = opts.scale;
  return t;
}
function icon(stack, name, size, color) {
  const sym = SFSymbol.named(name);
  sym.applyFont(Font.semiboldSystemFont(size));
  const im = stack.addImage(sym.image);
  im.imageSize = new Size(size, size);
  im.tintColor = col(color);
  return im;
}
function divider(stack, color) {
  const d = stack.addStack();
  d.size = new Size(0, 1);
  d.backgroundColor = col(color || C.card, 0.08);
  d.addSpacer();
}
function card(parent, accent, url) {
  const outer = parent.addStack();
  outer.layoutVertically();
  outer.cornerRadius = 14;
  outer.backgroundColor = col(C.card, 0.07);
  outer.borderWidth = 1;
  outer.borderColor = col(C.card, 0.10);
  if (url) outer.url = url;
  const strip = outer.addStack();
  strip.size = new Size(0, 3);
  const sg = new LinearGradient();
  sg.colors = [col(accent), col(accent, 0.15)];
  sg.locations = [0, 1];
  sg.startPoint = new Point(0, 0);
  sg.endPoint = new Point(1, 0);
  strip.backgroundGradient = sg;
  strip.addSpacer();
  const body = outer.addStack();
  body.layoutVertically();
  body.setPadding(8, 10, 8, 10);
  return { outer, body };
}
function cardTitle(body, sym, color, title, rightText) {
  const r = body.addStack();
  r.centerAlignContent();
  icon(r, sym, 10, color);
  r.addSpacer(6);
  text(r, title.toUpperCase(), 9.5, color, { weight: "bold" });
  r.addSpacer();
  if (rightText) text(r, rightText, 9.5, C.faint);
}
// ---- header ----
const hd = w.addStack();
hd.centerAlignContent();
const badge = hd.addStack();
badge.setPadding(2, 6, 2, 6);
badge.cornerRadius = 6;
const bgG = new LinearGradient();
bgG.colors = [col(C.cyan), col(C.purple), col(C.pink)];
bgG.locations = [0, 0.5, 1];
badge.backgroundGradient = bgG;
text(badge, "TC", 10, "#0a0a14", { weight: "heavy" });
hd.addSpacer(7);
text(hd, "Thrifty Curator · ${title}", 13, C.text, { weight: "semi" });
hd.addSpacer();
text(hd, "Updated " + new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), 9, C.faint);
w.addSpacer(6);
if (!data) {
  const { body } = card(w, C.pink);
  cardTitle(body, keyRejected ? "key.slash" : "wifi.exclamationmark", C.pink, keyRejected ? "Widget key rejected" : "Offline");
  body.addSpacer(6);
  text(body, keyRejected ? "This script's key is no longer valid. Open Admin → Operations → Widgets and re-copy the script." : "Couldn't reach the app. Tap to open.", 11, C.dim, { lines: 3 });
} else {
`;

export const footer = `}
if (config.runsInWidget) Script.setWidget(w);
else await (isLarge ? w.presentLarge() : isSmall ? w.presentSmall() : w.presentMedium());
Script.complete();
`;
